/**
 * Invoice Timeline (Apr 26, 2026; redesigned Feb 25, 2026 P2)
 *
 * Read-only chronological log of every billing event for one invoice:
 * lifecycle (generated, emailed, void/reopen) + payments (manual, bulk
 * CSV, partial / full).
 *
 * Design system (Feb 25, 2026):
 *   - Single `<TimelineRow>` shell used by every event type so spacing,
 *     typography, and pill style stay uniform across payments, lifecycle,
 *     and any future event class.
 *   - One title row · one meta row · optional one-line context · optional
 *     expandable per-order breakdown. No more inline mini-tables.
 *
 * Filter: all / payment / lifecycle.
 * Data: GET /api/pricing/billing/invoice/{id}/audit-log?events=...
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  History,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  User,
  FilePlus,
  Mail,
  RotateCcw,
  Ban,
  CircleDot,
} from 'lucide-react';
import { format as formatDateFns } from 'date-fns';
import { formatDateOnly, formatDateTimeLocal } from '../../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (n) =>
  (Number(n) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return formatDateTimeLocal(iso);
  } catch {
    return iso;
  }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return formatDateOnly(iso);
  } catch {
    return iso;
  }
};

// ---------------------------------------------------------------------
// Visual config per action
// ---------------------------------------------------------------------
const ACTION_META = {
  // payment events
  mark_orders_paid:                 { Icon: CheckCircle2, dot: 'bg-emerald-500', dotPartial: 'bg-amber-500' },
  payment_recorded:                 { Icon: CheckCircle2, dot: 'bg-emerald-500', dotPartial: 'bg-amber-500' },
  payment_recorded_via_bulk_upload: { Icon: CheckCircle2, dot: 'bg-emerald-500', dotPartial: 'bg-amber-500' },
  payment_recorded_via_bulk_upload_fee_override: { Icon: CheckCircle2, dot: 'bg-emerald-500', dotPartial: 'bg-amber-500' },
  // lifecycle events
  invoice_generated:    { Icon: FilePlus, dot: 'bg-blue-500' },
  invoice_regenerated:  { Icon: RotateCcw, dot: 'bg-blue-500' },
  invoice_emailed:      { Icon: Mail,     dot: 'bg-indigo-500' },
  invoice_email_failed: { Icon: Mail,     dot: 'bg-rose-500' },
  invoice_unbilled:     { Icon: RotateCcw, dot: 'bg-amber-500' },
  invoice_voided:       { Icon: Ban,      dot: 'bg-rose-500' },
  invoice_reopened:     { Icon: RotateCcw, dot: 'bg-blue-500' },
};

const FALLBACK_META = { Icon: CircleDot, dot: 'bg-slate-400' };

const LIFECYCLE_LABELS = {
  invoice_generated: 'Invoice generated',
  invoice_regenerated: 'Invoice regenerated',
  invoice_emailed: 'Invoice emailed',
  invoice_email_failed: 'Invoice email failed',
  invoice_unbilled: 'Invoice unbilled',
  invoice_voided: 'Invoice voided',
  invoice_reopened: 'Invoice reopened',
  payment_recorded: 'Payment recorded',
  payment_recorded_via_bulk_upload: 'Payment recorded via bulk upload',
};

const PAYMENT_ACTION_KEYS = new Set([
  'mark_orders_paid',
  'payment_recorded',
  'payment_recorded_via_bulk_upload',
  'payment_recorded_via_bulk_upload_fee_override',
]);

// INV-TIMELINE-SEQ-001: payment_date is a calendar date (bulk CSV / manual date-only) — never shift it across time zones.
const fmtPaymentDate = (entry) => (entry.payment_date_calendar ? fmtDate(entry.payment_date_calendar) : fmtDate(entry.payment_date));
const fmtTimeOnly = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : formatDateFns(d, 'h:mm:ss a');
};

// Truncate a long product-id list for the inline display.
const PRODUCT_ID_PREVIEW_LIMIT = 5;

// ---------------------------------------------------------------------
// Pills — keep the same visual language across every event type
// ---------------------------------------------------------------------
const Pill = ({ tone = 'slate', children, testId }) => {
  const tones = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-100 text-amber-700 border-amber-200',
    indigo:  'bg-indigo-100 text-indigo-700 border-indigo-200',
    slate:   'bg-slate-100 text-slate-600 border-slate-200',
    rose:    'bg-rose-100 text-rose-700 border-rose-200',
    blue:    'bg-blue-100 text-blue-700 border-blue-200',
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tones[tone] || tones.slate}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
};

// ---------------------------------------------------------------------
// Unified row shell — used by every entry type
// ---------------------------------------------------------------------
const TimelineRow = ({
  entryId,
  Icon,
  dotClass,
  title,
  subtitle,        // optional one-line context (e.g. "Product IDs: ...")
  metaItems,       // array of small text/JSX bits separated by middots
  expandable,      // optional expandable body { label, content }
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      className="relative pl-7 pb-4 last:pb-0"
      data-testid={`timeline-entry-${entryId}`}
    >
      <span className="absolute left-2 top-1 bottom-0 w-px bg-slate-200" aria-hidden />
      <span
        className={`absolute left-0 top-1 w-4 h-4 rounded-full ring-2 ring-white flex items-center justify-center ${dotClass}`}
        aria-hidden
      >
        <Icon className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </span>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">{title}</div>

          {subtitle && (
            <div className="mt-1 text-xs text-slate-600">{subtitle}</div>
          )}

          {metaItems?.length > 0 && (
            <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-slate-500">
              {metaItems.map((m, i) => (
                <React.Fragment key={i}>{m}</React.Fragment>
              ))}
            </div>
          )}
        </div>

        {expandable && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid={`timeline-toggle-${entryId}`}
          >
            {expanded ? (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Hide
              </>
            ) : (
              <>
                <ChevronRight className="w-3.5 h-3.5" />
                {expandable.label || 'Details'}
              </>
            )}
          </button>
        )}
      </div>

      {expandable && expanded && (
        <div className="mt-2">{expandable.content}</div>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
const buildProductIdSubtitle = (productIds) => {
  if (!productIds || productIds.length === 0) return null;
  const visible = productIds.slice(0, PRODUCT_ID_PREVIEW_LIMIT);
  const extra = productIds.length - visible.length;
  return (
    <span data-testid="timeline-product-ids">
      <span className="font-medium text-slate-700">Product IDs:</span>{' '}
      <span className="font-mono text-slate-700">{visible.join(', ')}</span>
      {extra > 0 && (
        <span className="text-slate-400"> · +{extra} more</span>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------
// Payment entry — unified for manual + bulk-upload + mark_orders_paid
// ---------------------------------------------------------------------
const PaymentEntryRow = ({ entry, showTime = false }) => {
  const meta = ACTION_META[entry.action] || ACTION_META.mark_orders_paid;
  const fullyPaid = entry.invoice_new_status === 'paid';
  const partiallyPaid = entry.invoice_new_status === 'partially_paid';
  const dotClass = fullyPaid ? meta.dot : (meta.dotPartial || meta.dot);
  const isBulk = (entry.action || '').startsWith('payment_recorded_via_bulk_upload');
  const hasSequence = Number.isFinite(entry.sequence_index) && Number.isFinite(entry.sequence_total);

  // The amount that goes in the title differs by action shape:
  // - mark_orders_paid: server returns `payment_amount` (sum of files).
  // - payment_recorded / bulk: server returns `amount`.
  const amount =
    entry.amount != null && entry.amount !== 0
      ? entry.amount
      : entry.payment_amount;

  const sourceLabel = isBulk
    ? 'recorded via bulk upload'
    : entry.action === 'mark_orders_paid'
      ? `${(entry.orders || []).length} file${(entry.orders || []).length === 1 ? '' : 's'} marked paid`
      : 'recorded';

  const title = (
    <>
      <span className="text-sm font-semibold text-slate-900 tabular-nums">
        {fmtCurrency(amount)}
      </span>
      <span className="text-xs text-slate-500">· {sourceLabel}</span>
      {hasSequence && (
        <Pill tone="slate" testId="timeline-payment-sequence">Payment {entry.sequence_index} of {entry.sequence_total}</Pill>
      )}
      {fullyPaid && (
        <Pill tone="emerald" testId="timeline-status-fully-paid">Fully Paid</Pill>
      )}
      {partiallyPaid && (
        <Pill tone="amber" testId="timeline-status-partial">Partial Payment</Pill>
      )}
      {isBulk && <Pill tone="indigo">Bulk Upload</Pill>}
      {entry.action === 'payment_recorded_via_bulk_upload_fee_override' && <Pill tone="rose">Fee Override</Pill>}
    </>
  );

  const productSubtitle = buildProductIdSubtitle(entry.product_ids);
  const subtitle = (productSubtitle || hasSequence) ? (
    <div className="flex flex-col gap-0.5">
      {productSubtitle}
      {hasSequence && (
        <span className="tabular-nums" data-testid="timeline-running-balance">
          <span className="font-medium text-slate-700">Paid {fmtCurrency(entry.paid_after)} of {fmtCurrency(entry.invoice_total)}</span>
          <span className="text-slate-400"> · </span>
          <span className={entry.balance_after > 0 ? 'text-amber-700 font-medium' : 'text-emerald-700 font-medium'}>
            Balance {fmtCurrency(entry.balance_after)}
          </span>
        </span>
      )}
    </div>
  ) : null;

  const metaItems = [
    <span key="d" title={fmtDateTime(entry.performed_at)} data-testid="timeline-payment-date">
      {entry.payment_date ? fmtPaymentDate(entry) : fmtDateTime(entry.performed_at)}
    </span>,
    showTime && entry.performed_at ? (
      <span key="t" className="text-slate-400" title="Time the payment was recorded" data-testid="timeline-payment-recorded-time">
        recorded {fmtTimeOnly(entry.performed_at)}
      </span>
    ) : null,
    entry.payment_reference ? (
      <span key="r" className="font-mono text-slate-700" data-testid="timeline-payment-reference">
        Ref: {entry.payment_reference}
      </span>
    ) : null,
    <span key="u" className="inline-flex items-center gap-1 text-slate-500">
      <User className="w-3 h-3" />
      {entry.performed_by_name || 'Unknown'}
    </span>,
  ].filter(Boolean);

  // Optional expandable per-order breakdown — only meaningful when the
  // entry carries multiple orders (mark_orders_paid). Keeps single-payment
  // bulk uploads scannable (their inline subtitle has the product IDs already).
  const orders = entry.orders || [];
  const expandable = orders.length > 1
    ? {
        label: `Files (${orders.length})`,
        content: (
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="tf-thead">
                <tr>
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Product ID</th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-slate-700">{o.order_number || '—'}</td>
                    <td className="px-3 py-1.5 font-medium text-slate-900 tabular-nums">{o.product_id || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-slate-800 tabular-nums">{fmtCurrency(o.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      }
    : null;

  return (
    <TimelineRow
      entryId={entry.id}
      Icon={CheckCircle2}
      dotClass={dotClass}
      title={title}
      subtitle={subtitle}
      metaItems={metaItems}
      expandable={expandable}
    />
  );
};

// ---------------------------------------------------------------------
// Lifecycle entry — invoice generated / emailed / void / reopen / etc.
// ---------------------------------------------------------------------
const LifecycleEntryRow = ({ entry }) => {
  const meta = ACTION_META[entry.action] || FALLBACK_META;
  const Icon = meta.Icon;
  const label = LIFECYCLE_LABELS[entry.action] || entry.action.replace(/_/g, ' ');

  const title = (
    <>
      <span className="text-sm font-semibold text-slate-900">{label}</span>
      <Pill tone="slate">Lifecycle</Pill>
    </>
  );

  let subtitle = null;
  if (entry.action === 'invoice_generated' || entry.action === 'invoice_regenerated') {
    subtitle = (
      <>
        <span className="font-semibold text-slate-700 tabular-nums">{fmtCurrency(entry.total_amount)}</span>
        <span className="text-slate-400"> · </span>
        <span>{entry.orders_count || 0} order{entry.orders_count === 1 ? '' : 's'}</span>
        {entry.client_code && (
          <>
            <span className="text-slate-400"> · </span>
            <span>{entry.client_code}</span>
          </>
        )}
      </>
    );
  } else if (entry.action === 'invoice_emailed' && entry.sent_to) {
    subtitle = (
      <span className="flex flex-col gap-0.5" data-testid="timeline-invoice-emailed">
        <span>
          Sent to <span className="font-mono text-slate-800">{entry.sent_to}</span>
          {entry.cc && <> · CC <span className="font-mono text-slate-800">{entry.cc}</span></>}
          {entry.invoice_amount != null && <> · <span className="font-medium text-slate-800">{fmtCurrency(entry.invoice_amount)}</span></>}
        </span>
        {entry.subject && <span className="text-slate-500 truncate" title={entry.subject}>Subject: {entry.subject}</span>}
        {entry.attachment_name && <span className="text-slate-400 font-mono text-[11px]">{entry.attachment_name}</span>}
      </span>
    );
  } else if (entry.action === 'invoice_email_failed') {
    subtitle = (
      <span className="text-rose-700" data-testid="timeline-invoice-email-failed">
        Email to <span className="font-mono">{entry.sent_to}</span> failed{entry.error ? ` — ${entry.error}` : ''}
      </span>
    );
  }

  const metaItems = [
    <span key="d" title={fmtDateTime(entry.performed_at)}>{fmtDateTime(entry.performed_at)}</span>,
    <span key="u" className="inline-flex items-center gap-1 text-slate-500">
      <User className="w-3 h-3" />
      {entry.performed_by_name || 'Unknown'}
    </span>,
  ];

  return (
    <TimelineRow
      entryId={entry.id}
      Icon={Icon}
      dotClass={meta.dot}
      title={title}
      subtitle={subtitle}
      metaItems={metaItems}
    />
  );
};

// ---------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------
const FILTERS = [
  { value: 'all', label: 'All events' },
  { value: 'payment', label: 'Payments' },
  { value: 'lifecycle', label: 'Lifecycle' },
];

export const PaymentHistoryTimeline = ({ invoiceId, refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!invoiceId) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    const fetchLog = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await axios.get(
          `${API}/pricing/billing/invoice/${invoiceId}/audit-log`,
          { params: { events: filter } }
        );
        if (!cancelled) setEntries(resp.data?.entries || []);
      } catch (e) {
        // INV-TIMELINE-001: a cancelled request (dialog closed / auth interceptor) is not an error
        if (cancelled || axios.isCancel?.(e) || e?.code === 'ERR_CANCELED') return;
        console.error('Failed to load invoice timeline:', e);
        const status = e.response?.status;
        const detail = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : null;
        setError(detail || (status ? `Failed to load invoice timeline (HTTP ${status})` : 'Failed to load invoice timeline — check your connection'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLog();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, refreshKey, filter, retryKey]);

  // INV-TIMELINE-SEQ-001: several payments on the same calendar date → show the recorded time so the order is explicit.
  const sameDayCounts = useMemo(() => {
    const counts = {};
    entries.forEach((e) => {
      if (e.event_type !== 'payment' && !PAYMENT_ACTION_KEYS.has(e.action)) return;
      const key = e.payment_date_calendar || (e.payment_date || e.performed_at || '').slice(0, 10);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [entries]);

  const renderEntry = useMemo(() => (entry, idx) => {
    const isPayment =
      entry.event_type === 'payment' ||
      PAYMENT_ACTION_KEYS.has(entry.action);
    const dayKey = entry.payment_date_calendar || (entry.payment_date || entry.performed_at || '').slice(0, 10);
    return isPayment ? (
      <PaymentEntryRow key={entry.id || idx} entry={entry} showTime={(sameDayCounts[dayKey] || 0) > 1} />
    ) : (
      <LifecycleEntryRow key={entry.id || idx} entry={entry} />
    );
  }, [sameDayCounts]);

  return (
    <div data-testid="invoice-timeline-section">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Invoice Timeline</h3>
          {entries.length > 0 && (
            <span className="text-xs text-slate-400">
              {entries.length} event{entries.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div
          className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5"
          data-testid="timeline-filter-pills"
        >
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              data-testid={`timeline-filter-${f.value}`}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${
                filter === f.value
                  ? 'bg-[#4F46E5] text-white shadow-sm'
                  : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-slate-400 py-4 text-center">
          Loading invoice timeline…
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center justify-between gap-3 text-xs text-red-600 py-2.5 px-3 rounded bg-red-50 border border-red-100" data-testid="invoice-timeline-error">
          <span>{error}</span>
          <button type="button" onClick={() => setRetryKey(k => k + 1)} className="inline-flex items-center gap-1 font-medium text-red-700 hover:underline" data-testid="invoice-timeline-retry">
            <RotateCcw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div
          className="text-xs text-slate-400 py-4 px-3 rounded bg-slate-50 border border-slate-100"
          data-testid="timeline-empty"
        >
          {filter === 'payment'
            ? 'No payment events yet.'
            : filter === 'lifecycle'
              ? 'No lifecycle events yet.'
              : 'No events yet for this invoice.'}
        </div>
      )}

      {!loading && !error && entries.length > 0 && (
        <ol className="max-h-[320px] overflow-y-auto pr-1">
          {entries.map(renderEntry)}
        </ol>
      )}
    </div>
  );
};

export default PaymentHistoryTimeline;
